import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import posthog from "posthog-js";
import { useNavigate } from "react-router-dom";
import CartService, { Cart, CartItem } from "../../_services/CartService";
import { stashCheckout } from "../embeddedCheckout";
import { stashQueuedAfterCheckout, storeQueueEntry } from "../queueStorage";
import { PAGE_ROUTES } from "../constants";

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  addToCart: (showId: string, quantity?: number, merchId?: string) => Promise<void>;
  addShippedMerch: (merchId: string, quantity?: number) => Promise<void>;
  updateItem: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<Cart | null>;
  checkout: (promoCode?: string) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function logCartDiagnostic(message: string, details?: unknown) {
  if (import.meta.env.DEV) {
    console.warn(`[cart] ${message}`, details);
  }
}

function cartContainsAddedItem(cart: Cart | null, addedItem: CartItem): boolean {
  if (!cart) return false;
  return cart.items.some(
    (item) =>
      item.id === addedItem.id &&
      item.quantity >= addedItem.quantity
  );
}

function computeTotals(items: CartItem[]): { total_price: number; total_items: number } {
  return {
    total_price: items.reduce((sum, item) => sum + item.total_price, 0),
    total_items: items.reduce((sum, item) => sum + item.quantity, 0),
  };
}

/** Patch the item into local cart state from a mutation response — avoids a
 * second round-trip (GET the whole cart) after every add/update, since the
 * add/update/remove endpoints already return everything needed to compute
 * the new totals client-side. */
function upsertItemLocal(cart: Cart | null, item: CartItem): Cart | null {
  if (!cart) return cart;
  const existingIndex = cart.items.findIndex((i) => i.id === item.id);
  const items =
    existingIndex >= 0
      ? cart.items.map((i, idx) => (idx === existingIndex ? item : i))
      : [...cart.items, item];
  return { ...cart, items, ...computeTotals(items) };
}

// Mirrors the server: removing a ticket also drops merch picked up at that show.
function removeItemLocal(cart: Cart | null, itemId: string): Cart | null {
  if (!cart) return cart;
  const removed = cart.items.find((i) => i.id === itemId);
  const dropsMerchFor = removed && !removed.merch ? removed.show?.id : undefined;
  const items = cart.items.filter(
    (i) => i.id !== itemId && !(dropsMerchFor && i.merch && i.show?.id === dropsMerchFor)
  );
  return { ...cart, items, ...computeTotals(items) };
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refreshCart = useCallback(async () => {
    try {
      const data = await CartService.getCart();
      setCart(data);
      return data;
    } catch (error) {
      logCartDiagnostic("Failed to refresh cart", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = async (showId: string, quantity: number = 1, merchId?: string) => {
    setLoading(true);
    try {
      const addedItem = await CartService.addToCart(showId, quantity, merchId);
      setCart((prev) => {
        const next = upsertItemLocal(prev, addedItem);
        if (!cartContainsAddedItem(next, addedItem)) {
          logCartDiagnostic("Local cart patch did not include item returned by add", {
            addedItem,
            next,
          });
        }
        return next;
      });
    } catch (error) {
      logCartDiagnostic("Error adding to cart", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const addShippedMerch = async (merchId: string, quantity: number = 1) => {
    setLoading(true);
    try {
      const addedItem = await CartService.addShippedMerch(merchId, quantity);
      setCart((prev) => {
        const next = upsertItemLocal(prev, addedItem);
        if (!cartContainsAddedItem(next, addedItem)) {
          logCartDiagnostic("Local cart patch did not include shipped merch returned by add", {
            addedItem,
            next,
          });
        }
        return next;
      });
    } catch (error) {
      logCartDiagnostic("Error adding shipped merch", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (itemId: string, quantity: number) => {
    setLoading(true);
    try {
      const updatedItem = await CartService.updateItem(itemId, quantity);
      setCart((prev) => (updatedItem ? upsertItemLocal(prev, updatedItem) : removeItemLocal(prev, itemId)));
    } catch (error) {
      logCartDiagnostic("Error updating cart item", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId: string) => {
    setLoading(true);
    try {
      await CartService.removeItem(itemId);
      setCart((prev) => removeItemLocal(prev, itemId));
    } catch (error) {
      logCartDiagnostic("Error removing cart item", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      await CartService.clearCart();
      setCart(null);
    } catch (error) {
      logCartDiagnostic("Error clearing cart", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const checkout = async (promoCode?: string) => {
    setLoading(true);
    try {
      posthog.capture("checkout_initiated", {
        item_count: cart?.items.length,
        total:      cart?.total_price,
      });
      const posthogDistinctId = posthog.get_distinct_id();
      const start = await CartService.checkout(promoCode, posthogDistinctId);
      const queued = start.queued ?? [];
      queued.forEach((q) => storeQueueEntry(q.show_id, q.entry_id));
      stashQueuedAfterCheckout(queued);
      if (!("session_id" in start)) {
        // Every show was busy: nothing to pay for yet, so straight to the line.
        await refreshCart();
        navigate(`${PAGE_ROUTES.QUEUE}/${queued[0].show_id}`);
        return;
      }
      stashCheckout(start);
      navigate(`${PAGE_ROUTES.CHECKOUT}?session_id=${encodeURIComponent(start.session_id)}`);
    } catch (error) {
      logCartDiagnostic("Error during checkout", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        addShippedMerch,
        updateItem,
        removeItem,
        clearCart,
        refreshCart,
        checkout,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
